import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Mutex } from 'async-mutex';
import {
  Subject,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  exhaustMap,
  from,
  pairwise,
  startWith,
  map,
} from 'rxjs';
import { SpaceRepository } from '../state/space.repository';
import { modifiedInterval, modifiedSetTimeout, modifiedThrottleTime } from '../utilities/ZoneUtils';
import { InspectModeRepository } from '../state/inspect-mode-repository.service';
import { DomListenerFactoryService } from './dom-listener-factory.service';
import { ProviderStateService } from './provider-state.service';
import {
  ISessionViewState,
  SessionSharedDataService,
  SessionView,
} from './session-shared-data.service';
import { TimerService } from './timer.service';
import { TelemetryService } from './telemetry.service';

const IDLENESS_PAUSE_DURATION_MS = 10000;

@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class UserIdleService {
  private userIdleSubjectManager: Map<
    number,
    { subject: Subject<boolean>; timeoutId: NodeJS.Timer }
  > = new Map();
  private userIdleSubjectMutex = new Mutex();
  private idleTimeouts: Array<number> = [];
  public onHeartbeat$ = new Subject<void>();
  private domListener = this.domListenerFactoryService.createInstance();
  private mouseListenerPauseTimeout?: NodeJS.Timer;
  private videoCallActivityIntervalSub?: Subscription;
  private _heartBeatTrigger$ = new Subject<void>();

  constructor(
    private readonly timerService: TimerService,
    private readonly spaceRepo: SpaceRepository,
    private readonly sharedDataService: SessionSharedDataService,
    private readonly providerStateService: ProviderStateService,
    private readonly domListenerFactoryService: DomListenerFactoryService,
    private readonly inspectModeRepository: InspectModeRepository,
    private readonly telemetryService: TelemetryService,
  ) {
    if (this.inspectModeRepository.isInspectMode) {
      // In inspect mode, we don't track user activity - presence heartbeat is handled
      // by RedisPresenceV2.startHeartbeat() which is triggered via getUserIdleSubject()
      return;
    }
    this._startListeningToPageActivity();

    this._heartBeatTrigger$
      .pipe(
        untilDestroyed(this),
        modifiedThrottleTime(IDLENESS_PAUSE_DURATION_MS, true, { leading: true, trailing: false }),
        exhaustMap(() => from(this.onHeartbeat())),
      )
      .subscribe();
  }

  /**
   * @param timeoutDurationInSeconds
   * Note: This function only returns activity state when user is in a space
   * @returns A subject which will notify if user is active/inactive (boolean)
   */
  public async getUserIdleSubject(timeoutDurationInSeconds: number): Promise<Subject<boolean>> {
    if (this.inspectModeRepository.isInspectMode) {
      // In inspect mode, return a subject that always reports user as active
      // This ensures presence is maintained and heartbeats continue
      const alwaysActiveSubject = new Subject<boolean>();
      // Immediately emit 'active' state to ensure presence system knows user is active
      alwaysActiveSubject.next(true);
      return alwaysActiveSubject;
    }
    return this.userIdleSubjectMutex.runExclusive(async () => {
      if (this.userIdleSubjectManager.has(timeoutDurationInSeconds)) {
        return this.userIdleSubjectManager.get(timeoutDurationInSeconds)!.subject;
      }
      const userIdleSubject = new Subject<boolean>();
      const timeoutId = await this.startIdleTimer(timeoutDurationInSeconds, userIdleSubject);
      this.userIdleSubjectManager.set(timeoutDurationInSeconds, {
        subject: userIdleSubject,
        timeoutId,
      });
      return userIdleSubject;
    });
  }

  private async onHeartbeat() {
    for (const [
      timeoutDurationInSeconds,
      { timeoutId, subject },
    ] of this.userIdleSubjectManager.entries()) {
      this.onHeartbeat$.next();
      await this.timerService.clearTimeout(timeoutId);
      const newTimeoutId = await this.timerService.setTimeout(() => {
        subject.next(false);
        this.idleTimeouts.push(timeoutDurationInSeconds);
      }, timeoutDurationInSeconds * 1000);
      this.userIdleSubjectManager.get(timeoutDurationInSeconds)!.timeoutId = newTimeoutId;

      if (this.idleTimeouts.length > 0) {
        for (const idleTimeoutDuration of this.idleTimeouts) {
          this.userIdleSubjectManager.get(idleTimeoutDuration)?.subject.next(true);
        }
        this.idleTimeouts = [];
      }
    }
  }

  private startIdleTimer(
    timeoutDurationInSeconds: number,
    userIdleSubject: Subject<boolean>,
  ): Promise<NodeJS.Timer> {
    return this.timerService.setTimeout(() => {
      userIdleSubject.next(false);
      this.idleTimeouts.push(timeoutDurationInSeconds);
    }, timeoutDurationInSeconds * 1000);
  }

  private _startListeningToPageActivity(): void {
    combineLatest([
      this.spaceRepo.activeSpaceId$.pipe(distinctUntilChanged()),
      this.sharedDataService.sessionView.current$.pipe(
        map((sessionView: ISessionViewState) => sessionView?.view === SessionView.WAITING_ROOM),
        distinctUntilChanged(),
      ),
      this.providerStateService.callConnected$.pipe(distinctUntilChanged()),
    ])
      .pipe(untilDestroyed(this), startWith([undefined, undefined, false]), pairwise())
      .subscribe(
        ([
          [prevSpaceId, _, prevCallConnected],
          [currSpaceId, isInWaitingRoom, currCallConnected],
        ]) => {
          const isEnteringSpace = !prevSpaceId && !!currSpaceId;
          const isInSpace = !!currSpaceId;
          const isjoiningCall = !prevCallConnected && !!currCallConnected;
          const isleavingCall = !!prevCallConnected && !currCallConnected;

          const logDataObject = {
            isEnteringSpace,
            isInSpace,
            isInWaitingRoom,
            isjoiningCall,
            isleavingCall,
          };

          // this order is importatnt for example if a user left a space directly while in a call
          // the order of firing will make the isleavingCall to be true in the last emission.
          // so we need to keep the following order of execution.
          if (!isInSpace && !isInWaitingRoom) {
            this.telemetryService.logBasicEvent('[activity_flow]', {
              action: 'stopActivityListeners',
              ...logDataObject,
            });
            this._stopIdleTimers();
            this.stopVideoCallActivityInterval();
            this.stopMouseListener();
            return;
          }

          if (isInWaitingRoom || isEnteringSpace || isleavingCall) {
            this.telemetryService.logBasicEvent('[activity_flow]', {
              action: 'startActivityMouseListener',
              ...logDataObject,
            });
            this.startActivityMouseListener();
            return;
          }

          if (isjoiningCall) {
            this.telemetryService.logBasicEvent('[activity_flow]', {
              action: 'startActivityVideoCallInterval',
              ...logDataObject,
            });
            this.startActivityVideoCallInterval();
            return;
          }
        },
      );
  }

  private startActivityMouseListener() {
    this.stopVideoCallActivityInterval();
    this.startMouseListener();
  }

  private startActivityVideoCallInterval() {
    this.stopMouseListener();
    this.startVideoCallActivityInterval();
  }

  private startVideoCallActivityInterval() {
    this.videoCallActivityIntervalSub = modifiedInterval(
      IDLENESS_PAUSE_DURATION_MS,
      true,
    ).subscribe(() => {
      this._heartBeatTrigger$.next();
    });
  }
  private stopVideoCallActivityInterval() {
    this.videoCallActivityIntervalSub?.unsubscribe();
  }

  /**
   * Called to stop existing idle timers when there is no active space
   */
  private _stopIdleTimers(): void {
    for (const { timeoutId } of this.userIdleSubjectManager.values()) {
      this.timerService.clearTimeout(timeoutId);
    }
  }

  private startMouseListener() {
    this._heartBeatTrigger$.next();
    this.stopMouseListener();
    this.domListener.add(
      'document',
      'mousemove',
      () => {
        this._heartBeatTrigger$.next();
        this.domListener.pauseAllListeners();
        this.mouseListenerPauseTimeout = modifiedSetTimeout(
          () => {
            this.domListener.resumeAllListeners();
          },
          IDLENESS_PAUSE_DURATION_MS,
          true,
        );
      },
      true,
    );
  }

  private stopMouseListener() {
    this.domListener.clear();
    if (this.mouseListenerPauseTimeout) {
      clearTimeout(this.mouseListenerPauseTimeout);
    }
  }
}
